import React, { useEffect, useState } from "react";
import { Card, CardBody, Col, Progress, Row, Spinner } from "reactstrap";
import CountUp from "react-countup";
import { getEngagementSummary } from "common/services/analytics.service";

interface SummaryMetric {
  visits: number;
  activeUsers: number;
  pagesVisited: number;
  activeAgencies: number;
  visitsPerUser: number;
}

interface Summary {
  current: SummaryMetric;
  previous: SummaryMetric;
  change: SummaryMetric;
  adoption: {
    registeredUsers: number;
    everActiveUsers: number;
    neverActiveUsers: number;
    adoptionRate: number;
  };
  stickiness: {
    dau: number;
    wau: number;
    mau: number;
    dauOverMau: number;
  };
}

/** Small up/down indicator against the preceding period of equal length. */
const Delta = ({ value, comparable }: { value: number; comparable: boolean }) => {
  if (!comparable) {
    return <span className="text-muted fs-12">vs. previous period n/a</span>;
  }

  const flat = value === 0;
  const up = value > 0;
  const tone = flat ? "text-muted" : up ? "text-success" : "text-danger";
  const icon = flat
    ? "ri-subtract-line"
    : up
    ? "ri-arrow-right-up-line"
    : "ri-arrow-right-down-line";

  return (
    <span className={`${tone} fs-12`}>
      <i className={`${icon} align-middle me-1`}></i>
      {Math.abs(value)}%<span className="text-muted"> vs. previous</span>
    </span>
  );
};

const StatTile = ({
  label,
  value,
  suffix,
  decimals,
  change,
  comparable,
  icon,
  tone,
}: {
  label: string;
  value: number;
  suffix?: string;
  decimals?: number;
  change: number;
  comparable: boolean;
  icon: string;
  tone: string;
}) => (
  <Col xl={3} md={6}>
    <Card className="card-animate">
      <CardBody>
        <div className="d-flex align-items-center">
          <div className="flex-grow-1 overflow-hidden">
            <p className="text-uppercase fw-medium text-muted text-truncate mb-0 fs-12">
              {label}
            </p>
          </div>
          <div className="flex-shrink-0">
            <div className="avatar-xs">
              <span
                className={`avatar-title bg-${tone}-subtle text-${tone} rounded-circle fs-16`}
              >
                <i className={icon}></i>
              </span>
            </div>
          </div>
        </div>
        <div className="d-flex align-items-end justify-content-between mt-3">
          <div>
            <h4 className="fs-22 fw-semibold mb-2">
              <CountUp
                start={0}
                end={value}
                decimals={decimals || 0}
                duration={2}
              />
              {suffix}
            </h4>
            <Delta value={change} comparable={comparable} />
          </div>
        </div>
      </CardBody>
    </Card>
  </Col>
);

const EngagementSummary = ({ typeOfRequest }: { typeOfRequest: string }) => {
  const [summary, setSummary] = useState<Summary | null>(null);
  const [comparable, setComparable] = useState<boolean>(true);
  const [loading, setLoading] = useState<boolean>(true);

  useEffect(() => {
    let cancelled = false;

    (async () => {
      setLoading(true);
      try {
        const response: any = await getEngagementSummary({ typeOfRequest });
        if (cancelled) return;
        setSummary(response?.data || null);
        setComparable(Boolean(response?.meta?.comparable));
      } catch (e) {
        if (!cancelled) setSummary(null);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [typeOfRequest]);

  if (loading) {
    return (
      <Col xs={12}>
        <Card>
          <CardBody className="text-center py-4">
            <Spinner color="primary" size="sm" />
          </CardBody>
        </Card>
      </Col>
    );
  }

  if (!summary) return null;

  const { current, change, adoption, stickiness } = summary;

  return (
    <React.Fragment>
      <StatTile
        label="Page Visits"
        value={current.visits}
        change={change.visits}
        comparable={comparable}
        icon="ri-eye-line"
        tone="primary"
      />
      <StatTile
        label="Active Users"
        value={current.activeUsers}
        change={change.activeUsers}
        comparable={comparable}
        icon="ri-user-follow-line"
        tone="success"
      />
      <StatTile
        label="Visits per User"
        value={current.visitsPerUser}
        decimals={1}
        change={change.visitsPerUser}
        comparable={comparable}
        icon="ri-repeat-line"
        tone="info"
      />
      <StatTile
        label="Active Agencies"
        value={current.activeAgencies}
        change={change.activeAgencies}
        comparable={comparable}
        icon="ri-building-line"
        tone="warning"
      />

      <Col xl={6}>
        <Card className="card-height-100">
          <CardBody>
            <div className="d-flex align-items-center mb-3">
              <h5 className="card-title mb-0 flex-grow-1">Adoption</h5>
              <span className="badge bg-primary-subtle text-primary fs-12">
                {adoption.adoptionRate}% of accounts used
              </span>
            </div>
            <p className="text-muted fs-13">
              How many registered accounts have ever visited a page.
            </p>
            <Progress
              value={adoption.adoptionRate}
              color="primary"
              className="mb-3"
              style={{ height: "8px" }}
            />
            <Row className="text-center g-0">
              <Col xs={4}>
                <div className="p-2 border border-dashed border-start-0">
                  <h5 className="mb-1">{adoption.registeredUsers}</h5>
                  <p className="text-muted mb-0 fs-12">Registered</p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="p-2 border border-dashed border-start-0">
                  <h5 className="mb-1 text-success">
                    {adoption.everActiveUsers}
                  </h5>
                  <p className="text-muted mb-0 fs-12">Ever active</p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="p-2 border border-dashed border-start-0 border-end-0">
                  <h5 className="mb-1 text-danger">
                    {adoption.neverActiveUsers}
                  </h5>
                  <p className="text-muted mb-0 fs-12">Never active</p>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>

      <Col xl={6}>
        <Card className="card-height-100">
          <CardBody>
            <div className="d-flex align-items-center mb-3">
              <h5 className="card-title mb-0 flex-grow-1">Stickiness</h5>
              <span className="badge bg-info-subtle text-info fs-12">
                {stickiness.dauOverMau}% DAU/MAU
              </span>
            </div>
            <p className="text-muted fs-13">
              Share of the last 30 days&apos; users who were active today.
              Always measured over rolling windows, independent of the period
              selected above.
            </p>
            <Progress
              value={stickiness.dauOverMau}
              color="info"
              className="mb-3"
              style={{ height: "8px" }}
            />
            <Row className="text-center g-0">
              <Col xs={4}>
                <div className="p-2 border border-dashed border-start-0">
                  <h5 className="mb-1">{stickiness.dau}</h5>
                  <p className="text-muted mb-0 fs-12">Today</p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="p-2 border border-dashed border-start-0">
                  <h5 className="mb-1">{stickiness.wau}</h5>
                  <p className="text-muted mb-0 fs-12">Last 7 days</p>
                </div>
              </Col>
              <Col xs={4}>
                <div className="p-2 border border-dashed border-start-0 border-end-0">
                  <h5 className="mb-1">{stickiness.mau}</h5>
                  <p className="text-muted mb-0 fs-12">Last 30 days</p>
                </div>
              </Col>
            </Row>
          </CardBody>
        </Card>
      </Col>
    </React.Fragment>
  );
};

export default EngagementSummary;
